import { useMemo, useRef } from "react";

/**
 * Bundle of refs used by the chat panel to track in-flight streaming state.
 *
 * These refs are deliberately co-located so they can be passed as a single
 * unit to other hooks (project boundary reset, execution sync, send handler).
 *
 * - `sessionIdRef`: persists the active sessionId across messages within a
 *   single conversation/component mount.
 * - `streamingMessageIdRef`: id of the AI placeholder message we're currently
 *   streaming text into (null between completed segments).
 * - `processedSegmentsRef`: how many `completedSegments` from Redux we've
 *   already consumed, so the execution-sync effect processes only new ones.
 *
 * The returned bundle is memoized so its identity is stable across renders.
 * Consumers (`useExecutionMessageSync`, `useSendChatMessage`) include this
 * object in their effect/callback dependency arrays — without the memo, the
 * fresh object literal returned each render would invalidate those deps every
 * commit, re-firing the execution-sync effect and recreating the send-message
 * callback on every parent re-render. That, combined with the fact that
 * `updateMessage` always produces a new conversations array (and thus another
 * re-render), creates a render loop during active streaming.
 *
 * The empty `useMemo` dep array is correct because the inner `useRef` objects
 * are themselves stable for the component's lifetime.
 */
export interface ChatStreamingRefs {
  sessionIdRef: React.MutableRefObject<string | null>;
  streamingMessageIdRef: React.MutableRefObject<string | null>;
  processedSegmentsRef: React.MutableRefObject<number>;
}

export function useChatStreamingRefs(): ChatStreamingRefs {
  const sessionIdRef = useRef<string | null>(null);
  const streamingMessageIdRef = useRef<string | null>(null);
  const processedSegmentsRef = useRef<number>(0);

  return useMemo(
    () => ({ sessionIdRef, streamingMessageIdRef, processedSegmentsRef }),
    // Refs are stable for the component's lifetime, so empty deps is correct.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
}
